/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React, { Component } from "react";
import "./VerifyCode.css";
import { simpleFetch } from "../../restWrapper";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import { Helmet } from "react-helmet";
import { DONOR_AUTH } from "../../config";
import errorMsg from "../../assets/json/errMsg.json";

class VerifyCode extends Component {
  constructor(props) {
    super(props);
    if (this.props.location.state === undefined) {
      if (this.props.history.length <= 1) {
        this.props.history.push("/");
        return;
      } else {
        this.props.history.push("/loginEmail");
        return;
      }
    }

    this.state = {
      code: "",
      errMsgs: [],
      isSubmitting: false,
    };

    this._handleCodeChange = this._handleCodeChange.bind(this);
    this.submitCode = this.submitCode.bind(this);
  }

  _handleCodeChange(event) {
    const value = event.target.value;
    // Only allow numeric input and max 6 digits
    if (/^\d{0,6}$/.test(value)) {
      this.setState({
        code: value,
        errMsgs: [],
      });
    }
  }

  submitCode() {
    if (this.state.isSubmitting) return;

    if (this.state.code.length !== 6) {
      this.setState({
        errMsgs: [this.props.languageObj.errorInvalidCode || "Please enter a valid 6-digit verification code."],
      });
      return;
    }

    this.setState({ isSubmitting: true });

    // isCustomDomain ensures x-checkplus-external is set even before a token
    // exists; once accessToken is present, simpleFetch also attaches Bearer.
    simpleFetch(
      "/donors/verify-code",
      "POST",
      {
        code: this.state.code,
      },
      false,
      { isCustomDomain: true }
    )
      .then((res) => {
        // Backend returns 200 with JWT + message, or legacy 204 with no body.
        // Treat both as success — otherwise the success message is shown as an error.
        if (res.status === 200 || res.status === 204) {
          if (res.status === 200 && res.body && res.body.accessToken) {
            localStorage.setItem("accessToken", res.body.accessToken);
            localStorage.setItem("expiredIn", res.body.expiredIn);
            if (res.body.emailVerified !== undefined) {
              localStorage.setItem(
                "emailVerified",
                res.body.emailVerified.toString()
              );
            }
          }

          // Mark donor as authenticated (same as seamless registration verify flow)
          const authObject = { status: true, timestamp: new Date().getTime() };
          localStorage.setItem(DONOR_AUTH, JSON.stringify(authObject));

          // Keep loading state until navigation completes
          this.props.history.push({
            pathname: "/DonorEmailVerified",
          });
        } else {
          this.setState({
            isSubmitting: false,
            errMsgs: [
              (res.body && res.body.message) ||
                errorMsg[this.props.language].donorInfoErr.verifyCodeFailed,
            ],
          });
        }
      })
      .catch((err) => {
        this.setState({
          isSubmitting: false,
          errMsgs: [errorMsg[this.props.language].donorInfoErr.networkErr],
        });
      });
  }

  render() {
    // The constructor already redirects away when location.state is missing
    // (direct visit / refresh wipes it). Bail out of this transitional render
    // so we don't dereference undefined state before that navigation lands.
    if (!this.props.location.state || !this.props.location.state.color) {
      return null;
    }

    const languageObj = this.props.languageObj;
    const organizationName = this.props.location.state.organizationName || "CheckPlus";

    const backgroundOverrideStyle = {
      background: "#" + this.props.location.state.color.headerColor,
    };

    const textOverrideStyle = {
      color: "#" + this.props.location.state.color.buttonColor,
    };

    const logoStyle = this.props.location.state.logoStyle;

    const handleKeyDown = (event) => {
      if (event.key === "Enter") {
        this.submitCode();
      }
    };

    return (
      <div className="VerifyCode-view">
        <Helmet>
          <title>{organizationName}</title>
        </Helmet>
        <div className="VerifyCode-prompt">
          <Link
            to={{
              pathname: "/EmailVerification",
              state: this.props.location.state,
            }}
          >
            <div className="VerifyCode-backContainer">
              <div className="VerifyCode-back">
                <div className="VerifyCode-backBtn"></div>
                <div className="VerifyCode-backText">{languageObj.backBtn}</div>
              </div>
            </div>
          </Link>

          <div
            className="VerifyCode-logoContainer"
            style={backgroundOverrideStyle}
          >
            {logoStyle && (
              <div className="VerifyCode-logo" style={logoStyle}></div>
            )}
          </div>

          <div className="VerifyCode-text">{languageObj.text}</div>
          <div className="VerifyCode-subText">{languageObj.subText}</div>

          <div className="VerifyCode-inputFieldContainer">
            <input
              onChange={this._handleCodeChange}
              type="text"
              className="VerifyCode-inputField"
              placeholder={languageObj.placeholder}
              value={this.state.code}
              onKeyDown={handleKeyDown}
              maxLength={6}
              inputMode="numeric"
              pattern="[0-9]*"
              disabled={this.state.isSubmitting}
            />
          </div>

          <div className="VerifyCode-inputFieldContainer">
            <div
              onClick={this.submitCode}
              className={`VerifyCode-submitBtn ${this.state.isSubmitting ? "VerifyCode-submitBtn-disabled" : ""}`}
              style={backgroundOverrideStyle}
            >
              <div className="VerifyCode-submitText" style={textOverrideStyle}>
                {this.state.isSubmitting
                  ? languageObj.submitting || "Verifying..."
                  : languageObj.submitBtn}
              </div>
            </div>
          </div>

          {this.state.errMsgs.map((err) => (
            <div key={err} className="VerifyCode-errorContainer">
              <div className="VerifyCode-errorIndicator"></div>
              <div className="VerifyCode-errorMessage">{err}</div>
            </div>
          ))}

          <div className="VerifyCode-chin"></div>
        </div>
      </div>
    );
  }
}

VerifyCode.propTypes = {
  language: PropTypes.string,
  languageObj: PropTypes.object,
};

export default VerifyCode;
